/**
 * Typed client for the shipped tenant-facing /api/agentic/* contract.
 *
 * Discovered from the backend source (do not invent endpoints):
 *   GET  /api/agentic/overview                    backend/app/routes/agentic_overview.py (P8 S3)
 *   GET  /api/agentic/safe-mode                   backend/app/routes/agentic_config.py
 *   GET  /api/agentic/config-approvals?status=    backend/app/routes/agentic_config.py (ApprovalOut[])
 *   POST /api/agentic/config-approvals            maker propose — UI: Safe-Mode exit request (PoliciesPage)
 *   POST /api/agentic/config-approvals/{id}/decide checker approve/reject — UI: ConfigApprovalsPage
 *   POST /api/agentic/safe-mode/enter | /exit     UI: PoliciesPage SafeModeControls (P12-AG03)
 *   POST /api/agentic/behavioral-events           (telemetry write — S3 shell does not emit)
 *   GET  /api/agentic/knowledge-health             backend/app/routes/agentic_knowledge.py
 *
 * Every endpoint is gated server-side on the agentic_platform entitlement
 * via require_features_or_404 (unentitled tenants get 404) plus
 * permissions: read-only GETs accept agentic:read OR agentic:config:*
 * (AG01); mutations still require agentic:config:propose/approve.
 * Client rendering is convenience only.
 */
import { apiGet, apiPost } from './client';

// --- GET /api/agentic/safe-mode -------------------------------------------
export interface SafeModeState {
  tenant_uuid: string;
  active: boolean;
  /** F4 (owner ruling 2026-09-16): true iff at least one ACTIVE user other
   *  than the caller holds agentic:config:approve — i.e. the brake could be
   *  released. When false the server refuses ENTER (409) and the UI
   *  disables the button, showing SAFE_MODE_ENTER_REFUSED_DETAIL. Null when
   *  the caller could not be resolved (F-S2-2) — treated as "not true". */
  exit_checker_available: boolean | null;
  /** Row 1121 (Fable delta re-review of 09155d789, doubt 2): true iff at
   *  least one ACTIVE user of the tenant holds agentic:config:propose —
   *  i.e. someone remains who could REQUEST and EXECUTE a Safe-Mode exit
   *  (POST /safe-mode/exit is itself propose-gated). NOT caller-exclusive
   *  (no self-decide rule on the propose side — the caller themselves
   *  counts) and never null: a plain tenant-wide fact, so it keeps
   *  reflecting reality even while Safe Mode is already active (e.g. it
   *  flips to false if the sole propose-holder who entered is later
   *  deactivated and nobody else ever held propose — the stuck-tenant
   *  scenario this row exists to surface). */
  propose_checker_available: boolean;
  /** Row 1121, adversarial review round 2 (F3): viewer-independent — does
   *  an active approve-holder and an active propose-holder who are two
   *  DIFFERENT people exist for this tenant right now. exit_checker_available
   *  and propose_checker_available are each counted on their OWN axis
   *  (relative to the caller, or to nobody) and can BOTH read true for a
   *  tenant that is actually stuck (the only person holding either
   *  permission is the same single person, so the self-decide rule blocks
   *  them from ever being their own second approver). This field is the
   *  one the UI banner keys off; the two counts above stay for display
   *  only. Never null. */
  exit_possible: boolean;
  entered_by: string | null;
  entered_at: string | null;
  reason: string | null;
  last_exited_by: string | null;
  last_exited_at: string | null;
  last_exit_approval_id: string | null;
}

// --- GET /api/agentic/config-approvals (ApprovalOut) -----------------------
export interface ConfigApproval {
  id: string;
  agent_id: string | null;
  change_type: string;
  proposed_change: Record<string, unknown>;
  requires_dual_control: boolean;
  maker_user_id: string;
  checker_user_id: string | null;
  status: string;
  proposed_at: string;
  decided_at: string | null;
}

// --- GET /api/agentic/overview ---------------------------------------------
export type RiskClassification = 'routine' | 'non_routine' | 'safety_critical';

export interface RecentDecision {
  id: string;
  agent_id: string;
  decision_type: string;
  risk_classification: RiskClassification;
  autonomy_phase: string;
  raw_confidence: number;
  calibrated_confidence: number | null;
  confidence_provenance: string;
  created_at: string | null;
  deadline_state: 'awaiting_delivery' | 'clock_running' | 'escalated' | 'closed' | null;
  latest_response_type: string | null;
}

export interface AgenticOverview {
  tenant_uuid: string;
  safe_mode: SafeModeState;
  decisions: {
    total: number;
    last_24h: number;
    by_risk: Record<RiskClassification, number>;
    open_supervised: number;
    escalated: number;
  };
  config_approvals: { pending: number };
  recent_decisions: RecentDecision[];
}

export const getOverview = () => apiGet<AgenticOverview>('/agentic/overview');

export const getSafeMode = () => apiGet<SafeModeState>('/agentic/safe-mode');

export const listConfigApprovals = (status?: string) =>
  apiGet<ConfigApproval[]>(
    `/agentic/config-approvals${status ? `?status=${encodeURIComponent(status)}` : ''}`,
  );

// --- GET /api/agentic/decisions (Decision Inbox list) ----------------------
// backend/app/routes/agentic_decisions.py
export interface InboxDecision {
  id: string;
  agent_id: string;
  decision_type: string;
  risk_classification: RiskClassification;
  autonomy_phase: string;
  raw_confidence: number;
  calibrated_confidence: number | null;
  confidence_provenance: string;
  action_recommended: Record<string, unknown> | null;
  created_at: string | null;
  deadline_state: 'awaiting_delivery' | 'clock_running' | 'escalated' | 'closed' | null;
  latest_response_type: string | null;
}

export interface DecisionListResponse {
  items: InboxDecision[];
  total: number;
  limit: number;
  offset: number;
}

export interface DecisionFilters {
  risk?: RiskClassification;
  agent_id?: string;
  phase?: string;
  limit?: number;
  offset?: number;
}

export const getDecisions = (filters: DecisionFilters = {}) => {
  const p = new URLSearchParams();
  if (filters.risk) p.set('risk', filters.risk);
  if (filters.agent_id) p.set('agent_id', filters.agent_id);
  if (filters.phase) p.set('phase', filters.phase);
  if (filters.limit != null) p.set('limit', String(filters.limit));
  if (filters.offset != null) p.set('offset', String(filters.offset));
  const qs = p.toString();
  return apiGet<DecisionListResponse>(`/agentic/decisions${qs ? `?${qs}` : ''}`);
};

// --- GET /api/agentic/decisions/{id} (decision card) -----------------------
export interface DecisionResponse {
  id: string;
  response_type: string;
  response_source: string;
  human_response: Record<string, unknown> | null;
  response_role: string | null;
  delivery_confirmed: boolean | null;
  passive_flag: boolean;
  created_at: string | null;
}

export interface DecisionDeadline {
  state: string;
  delivery_confirmed_at: string | null;
  override_deadline: string | null;
  max_hold_deadline: string | null;
  escalated_at: string | null;
}

export interface DecisionOutcome {
  execution_result: Record<string, unknown>;
  created_at: string | null;
}

export interface DecisionDetail {
  id: string;
  agent_id: string;
  decision_type: string;
  risk_classification: RiskClassification;
  autonomy_phase: string;
  raw_confidence: number;
  calibrated_confidence: number | null;
  confidence_provenance: string;
  action_recommended: Record<string, unknown> | null;
  reasoning_trace: Record<string, unknown> | null;
  evidence_refs: Record<string, unknown> | null;
  idempotency_key: string;
  versions: Record<string, string | null>;
  created_at: string | null;
  deadline: DecisionDeadline | null;
  responses: DecisionResponse[];
  outcomes: DecisionOutcome[];
}

export const getDecision = (id: string) =>
  apiGet<DecisionDetail>(`/agentic/decisions/${encodeURIComponent(id)}`);

// --- GET /api/agentic/agents (roster + trust) ------------------------------
// backend/app/routes/agentic_fleet.py
export interface AgentTrustMetric {
  decision_type: string;
  current_phase: string;
  total_decisions: number;
  accepted: number;
  modified: number;
  rejected: number;
  escalated: number;
  override_rate: number | null;
  avg_confidence: number | null;
  period_start: string | null;
  period_end: string | null;
}

export interface AgentRosterEntry {
  agent_id: string;
  capability_scope: Record<string, unknown> | null;
  role_mapping: Record<string, unknown> | null;
  autonomy_settings: Record<string, unknown> | null;
  approval_thresholds: Record<string, unknown> | null;
  escalation_chain: unknown;
  integrity_controls: Record<string, unknown> | null;
  config_version: string;
  updated_at: string | null;
  decision_count: number;
  trust: AgentTrustMetric[];
}

export interface AgentsResponse {
  tenant_uuid: string;
  agents: AgentRosterEntry[];
}

export const getAgents = () => apiGet<AgentsResponse>('/agentic/agents');

// --- GET /api/agentic/usage (usage + budget) -------------------------------
export interface UsageByAgent {
  agent_id: string;
  acu_units: number;
  customer_amount: number;
  event_count: number;
}

export interface UsageBudget {
  included_acu: number;
  hard_limit_acu: number;
  overage_allowed: boolean;
  soft_thresholds: Record<string, unknown> | null;
  period: string;
  utilization_pct: number | null;
}

export interface UsageResponse {
  tenant_uuid: string;
  totals: {
    acu_units: number;
    customer_amount: number;
    event_count: number;
    currency: string;
  };
  by_agent: UsageByAgent[];
  budget: UsageBudget | null;
}

export const getUsage = () => apiGet<UsageResponse>('/agentic/usage');

// --- GET /api/agentic/exceptions -------------------------------------------
// backend/app/routes/agentic_governance.py
export interface ExceptionItem {
  agent_decision_id: string;
  agent_id: string;
  decision_type: string;
  risk_classification: RiskClassification;
  raw_confidence: number;
  created_at: string | null;
  category: 'escalated' | 'rejected' | 'low_confidence' | (string & {});
  severity: 'high' | 'medium' | 'low' | (string & {});
  detail: string;
}

export interface ExceptionsResponse {
  tenant_uuid: string;
  total: number;
  by_category: Record<string, number>;
  items: ExceptionItem[];
}

export const getExceptions = () => apiGet<ExceptionsResponse>('/agentic/exceptions');

// --- GET /api/agentic/policies ---------------------------------------------
export interface SafetyPolicy {
  rule_id: string;
  rule_category: string;
  condition: Record<string, unknown> | null;
  requires: Record<string, unknown> | null;
  violation_behavior: string;
  regulatory_ref: string | null;
  severity: 'critical' | 'high' | 'medium' | (string & {});
  active: boolean;
  config_version: string;
  updated_at: string | null;
}

export interface PoliciesResponse {
  tenant_uuid: string;
  total: number;
  active: number;
  policies: SafetyPolicy[];
}

export const getPolicies = () => apiGet<PoliciesResponse>('/agentic/policies');

// --- GET /api/agentic/agents/{id} (agent detail) ---------------------------
// backend/app/routes/agentic_fleet.py
export interface AgentDecisionSummary {
  id: string;
  decision_type: string;
  risk_classification: RiskClassification;
  autonomy_phase: string;
  raw_confidence: number;
  calibrated_confidence: number | null;
  created_at: string | null;
}

export interface AgentDetail {
  agent_id: string;
  config: {
    capability_scope: Record<string, unknown> | null;
    role_mapping: Record<string, unknown> | null;
    autonomy_settings: Record<string, unknown> | null;
    approval_thresholds: Record<string, unknown> | null;
    escalation_chain: unknown;
    budget_limits: Record<string, unknown> | null;
    integrity_controls: Record<string, unknown> | null;
    config_version: string;
    updated_at: string | null;
  };
  trust: AgentTrustMetric[];
  recent_decisions: AgentDecisionSummary[];
}

export const getAgent = (id: string) =>
  apiGet<AgentDetail>(`/agentic/agents/${encodeURIComponent(id)}`);

// --- GET /api/agentic/runs (audit timeline) --------------------------------
// backend/app/routes/agentic_governance.py
export interface RunItem {
  id: string;
  agent_id: string;
  decision_type: string;
  risk_classification: RiskClassification;
  autonomy_phase: string;
  raw_confidence: number;
  confidence_provenance: string;
  versions: Record<string, string | null>;
  idempotency_key: string;
  created_at: string | null;
  latest_response_type: string | null;
  has_outcome: boolean;
}

export interface RunsResponse {
  tenant_uuid: string;
  total: number;
  items: RunItem[];
}

export const getRuns = () => apiGet<RunsResponse>('/agentic/runs');

// --- GET /api/agentic/knowledge-health --------------------------------------
// backend/app/routes/agentic_knowledge.py
export interface KnowledgeHealthSummary {
  total_manuals: number;
  with_extraction: number;
  extraction_coverage_pct: number;
  /** Raw 0.00–1.00 scale (Numeric(3,2)) — NOT a percent. Format ×100 in UI. */
  avg_confidence: number | null;
  generated_at: string;
}

export interface KnowledgeHealthExtraction {
  by_status: Record<string, number>;
  by_approval: Record<string, number>;
  failed: number;
  never_extracted: number;
}

export interface KnowledgeHealthGaps {
  manuals_unassigned_to_assets: number;
  manuals_never_extracted: number;
  extractions_failed: number;
}

export interface StalenessItem {
  manual_id: string;
  filename: string;
  manual_type: string | null;
  uploaded_at: string | null;
  last_extracted_at: string | null;
  extraction_status: string | null;
  days_since_extraction: number | null;
  severity: 'high' | 'medium' | 'low' | (string & {});
}

export interface KnowledgeHealth {
  tenant_uuid: string;
  summary: KnowledgeHealthSummary;
  extraction: KnowledgeHealthExtraction;
  gaps: KnowledgeHealthGaps;
  staleness_items: StalenessItem[];
}

export const getKnowledgeHealth = () => apiGet<KnowledgeHealth>('/agentic/knowledge-health');

// ---------------------------------------------------------------------------
// Governance WRITES (P12-AG03) — backend/app/routes/agentic_config.py.
// Each is permission-gated + audited server-side; the request bodies below
// are exactly the route schemas (DecisionIn / ProposalCreate / SafeModeEnterIn
// / SafeModeExitIn). No other write exists on this surface.
// ---------------------------------------------------------------------------

/** DecisionIn.decision — the backend knows no "defer"; a pending row stays
 *  pending until decided or expired (status CHECK: pending|approved|rejected|
 *  applied|expired). "Decide later" in the UI therefore sends nothing. */
export type ApprovalDecision = 'approve' | 'reject';

export const SAFE_MODE_EXIT_CHANGE_TYPE = 'safe_mode_exit';

/** Verbatim copy of backend safe_mode.ENTER_REFUSED_DETAIL (kept in lockstep). */
export const SAFE_MODE_ENTER_REFUSED_DETAIL =
  'Safe Mode cannot be entered: no other active administrator in this tenant holds ' +
  'the approve permission needed to authorize the exit. Grant agentic:config:approve ' +
  'to a second active administrator first. Requesting and executing the exit also ' +
  'requires a propose-holder.';

/** POST /config-approvals/{id}/decide — requires agentic:config:approve and
 *  checker <> maker (service + DB CHECK). */
export const decideConfigApproval = (id: string, decision: ApprovalDecision) =>
  apiPost<ConfigApproval>(`/agentic/config-approvals/${encodeURIComponent(id)}/decide`, {
    decision,
  });

/** POST /config-approvals {change_type:"safe_mode_exit"} — requires
 *  agentic:config:propose; 422 unless Safe Mode is currently active. */
export const proposeSafeModeExit = (reason: string | null) =>
  apiPost<ConfigApproval>('/agentic/config-approvals', {
    change_type: SAFE_MODE_EXIT_CHANGE_TYPE,
    reason,
  });

/** POST /safe-mode/enter — single actor (R6), requires agentic:config:propose. */
export const enterSafeMode = (reason: string | null) =>
  apiPost<SafeModeState>('/agentic/safe-mode/enter', { reason });

/** POST /safe-mode/exit — consumes an APPROVED safe_mode_exit approval
 *  (single use, 24h validity); requires agentic:config:propose. */
export const exitSafeMode = (approvalId: string) =>
  apiPost<SafeModeState>('/agentic/safe-mode/exit', { approval_id: approvalId });
